import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Grid from '../Grid/Grid';
import scopeCss from '../../utils/scopeCss';
import ArtworkControlsMenu from '../ArtworkControls/ArtworkControlsMenu';
import { createImageUrls } from '../../utils/ipfs';
import { createMuseumImageCache } from '../../utils/museumImageCache';

const ignore = () => {};

export default function MuseumPlayer({ exhibition, onStop, onSettings }) {
  const { settings, catalogue, entries } = exhibition;
  const [frame, setFrame] = useState(() => ({ index: settings.order === 'random' ? Math.floor(Math.random() * entries.length) : 0,
    view: settings.showGrid ? 'grid' : 'edition', serial: 0 }));
  const { index, view } = frame;
  const [fading, setFading] = useState(false);
  const [dark, setDark] = useState(settings.style === 'black' ? true : settings.style === 'white' ? false : null);
  const [held, setHeld] = useState(false);
  const [awake, setAwake] = useState(true);
  const [blackout, setBlackout] = useState(false);
  const [renderError, setRenderError] = useState('');
  const [unavailablePreviews, setUnavailablePreviews] = useState({});
  const [artEdges, setArtEdges] = useState(null);
  const [gridViewport, setGridViewport] = useState(() => window.innerWidth + 'x' + window.innerHeight);
  const workRef = useRef(null);
  const idleTimer = useRef(null);
  const [, setPreviewVersion] = useState(0);
  const previewCache = useRef(null);
  if (!previewCache.current) previewCache.current = createMuseumImageCache(() => setPreviewVersion(version => version + 1));
  useEffect(() => {
    const cache = previewCache.current;
    return () => cache.dispose();
  }, []);
  useEffect(() => {
    let timer;
    const resizeGrid = () => {
      clearTimeout(timer);
      // Fullscreen can settle after the initial render. Some deployed scripts
      // only size their canvas at construction and have no resize handler.
      timer = setTimeout(() => setGridViewport(window.innerWidth + 'x' + window.innerHeight), 120);
    };
    window.addEventListener('resize', resizeGrid);
    document.addEventListener('fullscreenchange', resizeGrid);
    document.addEventListener('webkitfullscreenchange', resizeGrid);
    resizeGrid();
    return () => {
      clearTimeout(timer); window.removeEventListener('resize', resizeGrid);
      document.removeEventListener('fullscreenchange', resizeGrid);
      document.removeEventListener('webkitfullscreenchange', resizeGrid);
    };
  }, []);
  const wake = useCallback(() => {
    setAwake(true); clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setAwake(false), 3000);
  }, []);
  useEffect(() => {
    wake();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    let lock = null, ended = false;
    const keepAwake = async () => {
      if (document.visibilityState !== 'visible' || !navigator.wakeLock) return;
      try { const next = await navigator.wakeLock.request('screen'); if (ended) next.release(); else lock = next; } catch (e) { /* Device policy may deny this. */ }
    };
    keepAwake();
    document.addEventListener('visibilitychange', keepAwake);
    const exit = e => { if (e.key === 'Escape') onStop(); };
    document.addEventListener('keydown', exit);
    const blockArtworkKeys = e => {
      if (settings.interaction || ['Tab', 'Escape'].includes(e.key)) return;
      if (e.target.closest && e.target.closest('.museum-transport')) return;
      e.preventDefault(); e.stopImmediatePropagation();
    };
    ['keydown', 'keyup', 'keypress'].forEach(type => window.addEventListener(type, blockArtworkKeys, true));
    return () => {
      ended = true; if (lock) lock.release().catch(() => {});
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('visibilitychange', keepAwake);
      document.removeEventListener('keydown', exit);
      ['keydown', 'keyup', 'keypress'].forEach(type => window.removeEventListener(type, blockArtworkKeys, true));
      clearTimeout(idleTimer.current);
    };
  }, [onStop, settings.interaction, wake]);
  useEffect(() => {
    if (held || (!settings.showGrid && entries.length < 2) || blackout || renderError) return;
    const transition = 1500;
    const duration = view === 'grid' ? settings.gridSeconds : settings.seconds;
    // Match the original: fade into the first edition immediately, return to
    // its grid at 20 seconds, then select another at 30 seconds by default.
    const delay = frame.serial === 0 && view === 'grid' ? 0 : Math.max(0, duration * 1000 - transition);
    let changeTimer;
    const timer = setTimeout(() => {
      setFading(true);
      changeTimer = setTimeout(() => {
        setFrame(previous => {
          if (previous.view === 'edition' && settings.showGrid) return { ...previous, view: 'grid', serial: previous.serial + 1 };
          const next = previous.serial === 0 && previous.view === 'grid' ? previous.index : settings.order === 'random'
            ? Math.floor(Math.random() * entries.length) : (previous.index + 1) % entries.length;
          return { index: next, view: 'edition', serial: previous.serial + 1 };
        });
        setFading(false);
      }, transition);
    }, delay);
    return () => { clearTimeout(timer); clearTimeout(changeTimer); };
  }, [frame, view, held, entries.length, settings.seconds, settings.gridSeconds, settings.showGrid, settings.order, blackout, renderError]);
  useEffect(() => {
    setRenderError('');
    if (view === 'grid') return;
    const timer = setTimeout(() => {
      if (!document.querySelector('#museum-stage canvas')) setRenderError('This edition has not produced a canvas. Check its renderer on this device, or advance to the next work.');
    }, 20000);
    return () => clearTimeout(timer);
  }, [index, view]);
  useEffect(() => {
    const work = workRef.current;
    let observed = new Set();
    let queued;
    const measure = () => {
      queued = null;
      const surfaces = [...work.querySelectorAll('canvas, img')];
      const bounds = surfaces.map(surface => surface.getBoundingClientRect()).filter(rect => rect.width > 0 && rect.height > 0);
      const current = new Set(surfaces);
      observed.forEach(surface => { if (!current.has(surface)) observer.unobserve(surface); });
      current.forEach(surface => { if (!observed.has(surface)) observer.observe(surface); });
      observed = current;
      if (!bounds.length) return;
      const left = Math.max(12, Math.min(...bounds.map(rect => rect.left)));
      const right = Math.min(window.innerWidth - 12, Math.max(...bounds.map(rect => rect.right)));
      const next = { left, width: Math.max(0, right - left) };
      setArtEdges(previous => previous && Math.abs(previous.left - next.left) < .5 && Math.abs(previous.width - next.width) < .5 ? previous : next);
    };
    const schedule = () => { if (!queued) queued = requestAnimationFrame(measure); };
    const observer = new ResizeObserver(schedule);
    observer.observe(work);
    const mutations = new MutationObserver(schedule);
    mutations.observe(work, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    schedule();
    return () => { cancelAnimationFrame(queued); observer.disconnect(); mutations.disconnect(); window.removeEventListener('resize', schedule); };
  }, [index, view]);

  const token = entries[index], item = catalogue[token.projectId];
  const css = settings.style === 'artist' ? item.css : settings.style === 'custom' ? settings.customCSS : '';
  const details = item.details || {};
  const gridTokens = useMemo(() => entries.filter(t => Number(t.projectId) === Number(token.projectId))
    .sort((a, b) => Number(a.editionId) - Number(b.editionId)), [entries, token.projectId]);
  const dimension = Math.max(1, Math.min(10, Number(item.project.maxGridDimension) || 3));
  const gridPage = Math.floor(gridTokens.findIndex(t => String(t.tokenId) === String(token.tokenId)) / (dimension * dimension)) + 1;
  const usePreviews = !!details.projectIdUseImageURLInGridView && !details.ipfsUnavailable && !unavailablePreviews[token.projectId];
  const gridPreviews = useMemo(() => usePreviews && details.projectIdToImageURLBase && details.projectIdToImageURLExt
    ? gridTokens.slice((gridPage - 1) * dimension * dimension, gridPage * dimension * dimension).map(edition => ({ tokenId: edition.tokenId,
      url: createImageUrls(details.projectIdToImageURLBase, edition.tokenId, details.projectIdToImageURLExt).primary })) : [],
  [gridTokens, gridPage, dimension, usePreviews, details.projectIdToImageURLBase, details.projectIdToImageURLExt]);
  useEffect(() => {
    if (settings.showGrid && view === 'edition') previewCache.current.preload(gridPreviews.map(preview => preview.url));
  }, [settings.showGrid, view, gridPreviews]);
  const preloadedImages = Object.fromEntries(gridPreviews.map(preview => [preview.tokenId, previewCache.current.get(preview.url)]));
  const go = direction => {
    setFrame(previous => ({ index: (previous.index + direction + entries.length) % entries.length, view: 'edition', serial: previous.serial + 1 }));
    setFading(false); setRenderError(''); wake();
  };
  const selectEdition = position => {
    const selected = gridTokens[Number(position) - 1];
    if (!selected || view !== 'grid') return;
    const chosen = entries.findIndex(t => String(t.tokenId) === String(selected.tokenId));
    if (chosen >= 0) { setFrame(previous => ({ index: chosen, view: 'edition', serial: previous.serial + 1 })); setFading(false); }
  };
  return createPortal(<section className={'museum-player ' + (awake ? 'is-awake' : 'is-idle')} aria-label={settings.name}
    data-project-id={token.projectId} data-edition-id={token.editionId} data-view={view} onPointerMove={wake} onPointerDown={wake} onFocusCapture={wake}>
    <div id="museum-stage" className={'museum-stage museum-style-' + settings.style + (settings.interaction ? '' : ' museum-no-interaction')}
      style={dark === null ? undefined : { backgroundColor: dark ? '#000' : '#fff', color: dark ? '#fff' : '#000' }}>
      {css && <style>{scopeCss(css, '#museum-stage')}</style>}
      <div ref={workRef} className={'museum-work museum-fade' + (fading ? ' fading-out' : '')}>
        <Grid key={view + ':' + token.projectId + (view === 'edition' ? ':' + token.tokenId : ':' + gridViewport)}
          selected={view === 'edition'} tokens={view === 'edition' ? token : gridTokens} script={item.script} scriptType={item.project.scriptType}
          maxGridDimension={dimension} currentPage={gridPage} handleClick={selectEdition} setMetadataObject={ignore} setSelectedEditionMetadata={ignore}
          projectIdToImageURLBase={details.projectIdToImageURLBase} projectIdToImageURLExt={details.projectIdToImageURLExt}
          preloadedImages={preloadedImages} clientViewImageURLInDetailView={false} projectIdUseImageURLInGridView={usePreviews}
          onIPFSUnavailable={() => setUnavailablePreviews(previous => ({ ...previous, [token.projectId]: true }))} />
      </div>
      {!settings.interaction && <div className="museum-interaction-cover" aria-hidden="true" />}
      {settings.labels && view === 'edition' && <div className="museum-wall-label" style={artEdges ? { left: artEdges.left, maxWidth: artEdges.width } : undefined}>
        <strong>{item.project.name} #{token.editionId}</strong><span>by {item.project.artist}</span>
        <a href={'https://immutables.art/#/' + token.projectId + '/' + token.editionId} target="_blank" rel="noopener noreferrer">immutables.art/#/{token.projectId}/{token.editionId}</a>
      </div>}
    </div>
    {blackout && <div className="museum-blackout" aria-label="Display blacked out" />}
    {renderError && <p className="museum-player-error" role="alert">{renderError}</p>}
    <nav className="museum-transport museum-classic-nav" style={artEdges ? { ...artEdges, transform: 'none' } : undefined} aria-label="Exhibition controls" onMouseEnter={wake}>
      <div className="museum-basic-controls">
      <details className="museum-more"><summary aria-label="More museum controls">•••</summary><div>
      <button onClick={() => go(-1)} disabled={entries.length < 2} aria-label="Previous work">Previous</button>
      <button onClick={() => { setHeld(!held); setFading(false); if (view === 'grid') setFrame(previous => ({ ...previous, view: 'edition', serial: previous.serial + 1 })); wake(); }} aria-pressed={held}>{held ? 'Resume sequence' : 'Hold this edition'}</button>
      <button onClick={() => go(1)} disabled={entries.length < 2} aria-label="Next work">Next</button>
      <span>{index + 1} / {entries.length}</span>
      <button onClick={() => { setBlackout(!blackout); setFading(false); wake(); }} aria-pressed={blackout}>{blackout ? 'Restore display' : 'Blackout'}</button>
      <button onClick={() => { if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen().catch(() => {}); wake(); }}>Fullscreen</button>
      <button onClick={onSettings}>Settings</button><button onClick={onStop}>Stop</button>
      </div></details>
        <button className="font-display" onClick={onStop}>stop museum mode</button>
        {view === 'edition' && settings.interaction && <ArtworkControlsMenu />}
        <button className="font-display" onClick={() => { setDark(!dark); wake(); }}>{dark ? 'light' : 'dark'}</button>
        <button aria-label="Museum settings" title="Customize museum mode" onClick={onSettings}>⚙</button>
      </div>
      <a className="museum-project-callout font-display" href={'https://immutables.art/#/' + token.projectId + (view === 'edition' ? '/' + token.editionId : '')} target="_blank" rel="noopener noreferrer">
        {item.project.name}{view === 'edition' ? ' # ' + token.editionId : ''}
      </a>
    </nav>
  </section>, document.body);
}
